import { Component, forwardRef, Input, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule, FormsModule, UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { DropDownList, DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { EnumTranslationService } from '../../services/enum-translation.service';
import { DropDownItemTyped } from '../drop-down-item.model';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-enum-drop-down-list',
    templateUrl: './enum-drop-down-list.component.html',
    styleUrls: ['./enum-drop-down-list.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => EnumDropDownListComponent),
            multi: true,
        }
    ],
    standalone: true,
    imports: [
        DropDownListModule,
        ReactiveFormsModule,
        FormsModule,
        TranslateModule,
    ],
})
export class EnumDropDownListComponent implements ControlValueAccessor {
  @Input() public showClearButton = true;
  @Input() public placeholder = '';
  @Input() public width = '200';

  @Input() public set enumType(value: any) {
    if (this._enumType === value) {
      return;
    }
    this._enumType = value;
    this.init();
  }

  @Input() public set baseTranslationString(value: string) {
    if (this._baseTranslationString === value) {
      return;
    }
    this._baseTranslationString = value;
    this.init();
  }

  /**
   * Excludes enum members from the dropdown list.
   */
  @Input() public set excludeEnumMembers(value: number[]) {
    if (this._excludeEnumMembers === value) {
      return;
    }
    this._excludeEnumMembers = value;
    this.init();
  }

  @ViewChild('dropDownList')
  private dropDownList: DropDownList;

  public enumSelectionData: DropDownItemTyped<any>[];

  public fields = {text: 'text', value: 'value'};

  public form: UntypedFormGroup;

  private _enumType: any; // unknown since enum type we want to use is not known yet
  private _baseTranslationString: string;
  private _excludeEnumMembers: number[] = [];
  private subscriptions: Subscription[] = [];

  constructor(private formBuilder: UntypedFormBuilder, private enumTranslationService: EnumTranslationService) {
    this.form = this.formBuilder.group({
      formValue: null
    });
    this.form.controls.formValue.disable();

    this.subscriptions.push(
      // any time the inner form changes update the parent of any change
      this.form.valueChanges.subscribe(value => {
        this.onChange(value?.formValue);
        this.onTouched();
      })
    );
   }

  public onChange: any = () => {};
  public onTouched: any = () => {};

  public writeValue(value: any): void {
    this.form.controls.formValue.setValue(value);
  }

  public registerOnChange(fn): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.controls.formValue.disable();
    } else {
      this.form.controls.formValue.enable();
    }
  }

  private init() {
    if (!this._enumType || !this._baseTranslationString) {
      return;
    }
    this.enumSelectionData = this.enumTranslationService.translateNumericEnum(this._enumType, this._baseTranslationString, this._excludeEnumMembers);
    this.dropDownList?.refresh();
  }
}
